import { useEffect, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router'

import { ButtonsPane, Checkbox, Form, Paper, SafeRouting, TextInput } from '../common/Form'
import Pendable from '../common/Pendable'
import Wrapper from '../common/Wrapper'
import { Api, AppDispatch, GlobalState, useRoutes } from '../../store'
import { ListPortSortableField, Network, Role } from 'common/api/v1/types'
import {
  alarmsThatDisablePort,
  formTransform,
  getApplianceOwnerId,
  getInterfaceName,
  isEditableAppliance,
  isEditableGroup,
  isEditableInterface,
  pluralize,
  useConfirmationDialog,
  useSettingsSelector,
  useUser,
} from '../../utils'
import {
  clearNetwork,
  createNetwork,
  getNetwork,
  removeNetwork,
  updateNetwork,
} from '../../redux/actions/networkActions'
import RHF, { FormProps } from '../common/Form/RHF'
import Typography from '@mui/material/Typography'
import PaginatedList from '../common/SelfStatePaginatedList'
import { EnrichedPhysicalPort, PortsRequestParams } from '../../api/nm-types'
import { MoveToEntity, Table, TableConfig } from '../common/Table'
import { Link } from '../common/Link'

const { portsApi } = Api

const NetworkForm = ({ getValues, setError, setFocus, formState }: FormProps<Network | Omit<Network, 'id'>>) => {
  const values = getValues()
  const navigate = useNavigate()
  const routes = useRoutes()
  const user = useUser()
  const setConfirmation = useConfirmationDialog()
  const dispatch = useDispatch<AppDispatch>()
  const { formErrors, saving } = useSelector(
    ({ networksReducer }: GlobalState) => ({ formErrors: networksReducer.formErrors, saving: networksReducer.saving }),
    shallowEqual,
  )
  const { devMode } = useSettingsSelector()

  useEffect(() => {
    if (Array.isArray(formErrors)) {
      formErrors.forEach((item) => {
        setError(item.name as keyof Network, { type: 'manual', message: item.reason })
      })
      if (formErrors[0]) setFocus(formErrors[0].name as keyof Network)
    }
  }, [formErrors, setError, setFocus])

  const deleteNetwork = () =>
    'id' in values &&
    setConfirmation(
      () => void dispatch(removeNetwork(values.id)),
      `Are you sure you want to remove network ${values.name}?`,
      undefined,
      { ok: { text: 'remove', color: 'error' } },
    )

  return (
    <>
      <SafeRouting formState={formState} />
      <Form id="network-form" noValidate>
        <Paper
          title="Network"
          tooltip={
            <>
              <p>Interfaces within the same network will be connected using their private IP addresses.</p>
              <p>To assign networks to interfaces navigate to an appliance or a particular interface.</p>
              <p>Networks can be temporarily disabled to switch over to the behavior defined by address mappings.</p>
            </>
          }
        >
          <TextInput name="name" label="Name" required autoFocus />
          <Checkbox name="enabled" label="Enable" />
        </Paper>
        <ButtonsPane
          main={{
            Cancel: {
              onClick: () => void navigate(routes.networks()),
            },
            Save: { savingState: !!saving, primary: true, type: 'submit' },
          }}
          secondary={
            'id' in values && user.role === Role.super
              ? {
                  'Remove network': { onClick: deleteNetwork },
                }
              : undefined
          }
        />
      </Form>

      {'id' in values && devMode && <NetworkInterfaces network={values.id} />}
    </>
  )
}

export default function EditNetwork() {
  const { id } = useParams()
  const dispatch = useDispatch<AppDispatch>()
  useEffect(() => {
    id && dispatch(getNetwork(id))
    return () => {
      dispatch(clearNetwork())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const networkToEdit = useSelector(({ networksReducer }: GlobalState) => networksReducer.network, shallowEqual)
  const showConfirmation = useConfirmationDialog()
  const onSubmit = (network: Network | Omit<Network, 'id'>) => {
    if (networkToEdit) {
      if (network.enabled === networkToEdit.enabled) return dispatch(updateNetwork(network as Network))
      showConfirmation(
        () => void dispatch(updateNetwork(network as Network)),
        `${network.enabled ? 'Enabling' : 'Disabling'} the network may cause disruptions in active routing services.
          Are you sure you want to proceed?`,
      )
    } else dispatch(createNetwork(network))
  }
  const user = useUser()
  if (!user || user.role !== Role.super) return null

  return (
    <Wrapper name={['Networks', id ? networkToEdit?.name : 'New']}>
      <Pendable pending={!!id && !networkToEdit}>
        <RHF
          onSubmit={(values) => {
            onSubmit(formTransform(values))
          }}
          defaultValues={
            ((networkToEdit as Network) || ({ name: '', enabled: true } as Omit<Network, 'id'>)) as
              | Network
              | Omit<Network, 'id'>
          }
          component={NetworkForm}
        />
      </Pendable>
    </Wrapper>
  )
}

const NetworkInterfaces = ({ network }: { network: string | undefined }) => {
  const routes = useRoutes()
  const user = useUser()

  const tableConfig: TableConfig<EnrichedPhysicalPort, ListPortSortableField> = [
    {
      title: 'name',
      getValue: (port) => {
        return (
          <Link
            to={routes.interfacesUpdate({ id: port.id })}
            available={!!port._appliance && isEditableInterface(getApplianceOwnerId(port._appliance), user)}
            underline="hover"
          >
            {getInterfaceName(port)}
          </Link>
        )
      },
      sorting: { byParameter: ListPortSortableField.portName },
    },
    {
      title: 'address',
      getValue: (port) => port.addresses[0]?.address,
      sorting: { byParameter: ListPortSortableField.address },
    },
    {
      title: 'public address',
      getValue: (port) => port.addresses[0]?.publicAddress,
      sorting: { byParameter: ListPortSortableField.publicAddress },
    },
    {
      title: 'appliance',
      getValue: ({ appliance: { name, id }, _appliance }) => (
        <Link
          to={routes.appliancesUpdate({ id })}
          underline="hover"
          available={!!_appliance && isEditableAppliance(user, getApplianceOwnerId(_appliance))}
        >
          {name}
        </Link>
      ),
      sorting: { byParameter: ListPortSortableField.applianceName },
    },
    {
      title: 'owner group',
      getValue: ({ _owner }) => (
        <Link
          to={routes.groupsUpdate({ id: _owner.id })}
          available={isEditableGroup(_owner.id, user)}
          underline="hover"
        >
          {_owner.name}
        </Link>
      ),
      sorting: { byParameter: ListPortSortableField.ownerGroupName },
    },
    {
      title: 'alarms',
      getValue: ({ _appliance, id: portId }) => {
        const alarms = _appliance?.alarms.filter((alarm) => alarm.physicalPortId == portId)
        if (!alarms || alarms.length === 0) return 'no alarms'
        const portDisabledAlarms = alarmsThatDisablePort(_appliance, portId)
        if (portDisabledAlarms && portDisabledAlarms.length > 0) {
          return (
            <>
              {pluralize(alarms.length, 'alarm')}, interface is disabled
              {portDisabledAlarms.map((portAlarm, i) => {
                return (
                  <Typography
                    key={portAlarm.alarmId.concat(i.toString())}
                    component="div"
                    variant="body2"
                    color="textSecondary"
                  >
                    {portAlarm.text}
                  </Typography>
                )
              })}
            </>
          )
        }

        return <>{pluralize(alarms.length, 'alarm')}</>
      },
    },
    {
      getValue: ({ id: portId, _appliance }) =>
        !!_appliance && isEditableInterface(getApplianceOwnerId(_appliance), user) ? (
          <MoveToEntity
            url={routes.interfacesUpdate({ id: portId })}
            entityName="interface"
            dataTestId={`edit-${portId}`}
          />
        ) : null,
      props: { align: 'right', noTypography: true },
    },
  ]

  const [sortProps, setSortProps] = useState<{ asc?: ListPortSortableField } | { desc?: ListPortSortableField }>({})
  const [refreshData, setRefreshData] = useState(false)
  const setNewSortProps = (newValue: any) => {
    setSortProps(newValue)
    setRefreshData((prevState) => !prevState)
  }

  return (
    <Paper title="Interfaces">
      <PaginatedList<PortsRequestParams, EnrichedPhysicalPort>
        api={(requestParams) => portsApi.getPorts({ ...requestParams, ...sortProps, network })}
        emptyMessage="no interfaces found"
        notFoundMessage="no matching interfaces"
        updateOn={refreshData}
        List={({ list }) => (
          <Table
            data={list}
            sorting={{
              currentSortKey: sortProps,
              onSortOrderChanged: setNewSortProps,
            }}
            emptyMessageComponent="no interfaces found"
            config={tableConfig}
          />
        )}
      />
    </Paper>
  )
}
